import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, CalendarDays, ScanLine, Users, Menu as MenuIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Bottom tab bar (mobile-first, Instagram/Airbnb style).
 * 5 round icons — the 5th (Menu) opens the drawer with the remaining sections.
 * Sticky at the bottom, floats above content with a subtle blur.
 */
export default function MobileTabBar({ onOpenMenu }) {
  const { t } = useTranslation();
  const location = useLocation();
  const isCalendarActive = location.pathname.startsWith("/app/calendar");

  const tabs = [
    { to: "/app",           end: true,  Icon: Home,         label: t("nav.dashboard"), testid: "tab-home" },
    { to: "/app/calendar",  end: false, Icon: CalendarDays, label: t("nav.calendar"),  testid: "tab-calendar", active: isCalendarActive },
    { to: "/app/scanner",   end: false, Icon: ScanLine,     label: t("nav.scanner"),   testid: "tab-scan", primary: true },
    { to: "/app/guests",    end: false, Icon: Users,        label: t("nav.guests"),    testid: "tab-guests" },
  ];

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[70] pointer-events-none"
      data-testid="mobile-tab-bar"
    >
      <div className="mx-auto max-w-lg px-4 pb-4 pointer-events-auto" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
        <motion.nav
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="rounded-full bg-white/90 backdrop-blur-lg border border-slate-200 shadow-[0_10px_40px_-10px_rgba(15,23,42,0.25)] flex items-center justify-around px-2 py-2"
        >
          {tabs.map((tab) => (
            <TabItem key={tab.to} {...tab} />
          ))}
          <button
            type="button"
            onClick={onOpenMenu}
            data-testid="tab-menu"
            aria-label={t("nav.menu", "Menu")}
            className="w-11 h-11 flex flex-col items-center justify-center rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
          >
            <MenuIcon className="w-5 h-5" strokeWidth={2} />
            <span className="text-[9px] font-medium mt-0.5">{t("nav.more", "Altro")}</span>
          </button>
        </motion.nav>
      </div>
    </div>
  );
}

function TabItem({ to, end, Icon, label, testid, primary, active }) {
  if (primary) {
    return (
      <NavLink to={to} end={end} data-testid={testid} aria-label={label}
        className="-mt-6 w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-[0_10px_25px_-6px_rgba(37,99,235,0.6)] hover:from-blue-500 hover:to-blue-600 transition-all">
        <Icon className="w-6 h-6" strokeWidth={2} />
      </NavLink>
    );
  }
  return (
    <NavLink to={to} end={end} data-testid={testid} aria-label={label}
      className={({ isActive }) => {
        const on = isActive || active;
        return `w-11 h-11 flex flex-col items-center justify-center rounded-full transition-colors ${
          on ? "text-blue-600" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
        }`;
      }}>
      <Icon className="w-5 h-5" strokeWidth={2} />
      <span className="text-[9px] font-medium mt-0.5 truncate max-w-[52px]">{label}</span>
    </NavLink>
  );
}
